import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import TrustdService from "../service/TrustdService";
import { useAppContext } from "../context/AppContext";
import { apiHeaders } from "../hooks";
import { useLocation, useNavigate } from "../router";
import trustdLogo from "../assets/image.webp";

const menuItems = [
  { label: "Home", to: "/", icon: "pi pi-home" },
  { label: "My Guests", to: "/reviews", icon: "pi pi-users" },
  { label: "My Listings", to: "/listings", icon: "pi pi-building" },
  { label: "Top Up Credits", to: "/topup", icon: "pi pi-credit-card" },
  { label: "FAQ", to: "/faq", icon: "pi pi-question-circle" },
  { label: "Account", to: "/account", icon: "pi pi-user" },
  { label: "Contact Support", to: "/contact", icon: "pi pi-comments" },
];
const CREDITS_REFRESH_INTERVAL_MS = 30 * 1000;
const SUPPORTED_PMS_CHANNELS = ["ownerrez", "hostaway", "hospitable"];

const formatChannelLabel = (channel = "") => {
  const normalized = String(channel || "").trim();
  return normalized
    ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    : "None";
};

const checkPmsLink = async (details) => {
  const email = String(details?.email || details?.username || "")
    .trim()
    .toLowerCase();
  const channel = String(details?.channel || "")
    .trim()
    .toLowerCase();
  if (!email || !SUPPORTED_PMS_CHANNELS.includes(channel)) return null;

  let response = null;
  if (channel === "ownerrez") {
    response = await axios.get(`/v1/integrations/tpc/ownerrez/user/${email}`, {
      headers: apiHeaders,
      timeout: 0,
    });
  }
  if (channel === "hostaway") {
    response = await axios.get(`/v1/integrations/tpc/hostaway/user/${email}`, {
      headers: apiHeaders,
      timeout: 0,
    });
  }
  if (channel === "hospitable") {
    response = await axios.get(
      `/v1/integrations/stripe/pms/hospitableAuthCheck/${email}`,
      { headers: apiHeaders, timeout: 0 },
    );
  }

  if (!response) return null;
  const status = String(response.data?.status || "")
    .trim()
    .toLowerCase();
  return !(response.data === "" || status === "error");
};

export default function Layout({ children }) {
  const { state, commit } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileTopbarOpen, setMobileTopbarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [user, setUser] = useState(null);
  const [pmsLinked, setPmsLinked] = useState(null);
  const [pmsStatusLoading, setPmsStatusLoading] = useState(false);
  const creditsRefreshInFlightRef = useRef(false);

  const refreshUserInfo = useCallback(async () => {
    if (creditsRefreshInFlightRef.current) return;
    creditsRefreshInFlightRef.current = true;
    try {
      const detailed = await TrustdService.getUserInformationByHostEmail({
        forceRefresh: true,
      });
      if (detailed) {
        setUser(detailed);
        const channel = String(detailed?.channel || "")
          .trim()
          .toLowerCase();
        if (SUPPORTED_PMS_CHANNELS.includes(channel)) {
          setPmsStatusLoading(true);
          try {
            setPmsLinked(await checkPmsLink(detailed));
          } finally {
            setPmsStatusLoading(false);
          }
        } else {
          setPmsLinked(null);
          setPmsStatusLoading(false);
        }
      }
    } catch (error) {
      console.error("Topbar user load failed:", error);
      setPmsLinked(null);
      setPmsStatusLoading(false);
    } finally {
      creditsRefreshInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    commit("shouldHideMenuNavigation", true);
  }, [commit]);

  useEffect(() => {
    if (!state.isUserSignedIn) {
      setUser(null);
      return;
    }

    let active = true;
    const safeRefresh = async (options = {}) => {
      if (!active) return;
      await refreshUserInfo(options);
    };

    void safeRefresh();

    const intervalId = window.setInterval(() => {
      void safeRefresh();
    }, CREDITS_REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void safeRefresh();
      }
    };
    const handleWindowFocus = () => {
      void safeRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("trustd:pms-link-updated", safeRefresh);

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("trustd:pms-link-updated", safeRefresh);
    };
  }, [refreshUserInfo, state.isUserSignedIn]);

  useEffect(() => {
    if (!state.isUserSignedIn) return;
    // Route transitions can follow credit-changing actions (top-up/screening).
    void refreshUserInfo();
  }, [location.pathname, refreshUserInfo, state.isUserSignedIn]);

  useEffect(() => {
    setMobileOpen(false);
    setMobileTopbarOpen(false);
  }, [location.pathname]);

  const logout = async () => {
    setMobileOpen(false);
    setMobileTopbarOpen(false);
    await AuthService.signOut();
    commit("signOutUser");
    commit("shouldHideMenuNavigation", false);
    navigate("/login");
  };

  const goTo = (path) => {
    setMobileOpen(false);
    setMobileTopbarOpen(false);
    navigate(path);
  };

  const displayName = user?.contactName || "Account";
  const screeningCredits = Number(user?.screening || 0);
  const pmsChannel = String(user?.channel || "")
    .trim()
    .toLowerCase();
  const pmsChannelLabel = formatChannelLabel(pmsChannel);
  const hasSupportedPms = SUPPORTED_PMS_CHANNELS.includes(pmsChannel);
  const pmsStatusLabel = pmsStatusLoading
    ? "Checking PMS..."
    : hasSupportedPms && pmsLinked === false
      ? `${pmsChannelLabel} not connected`
      : `Connected to ${pmsChannelLabel}`;
  const pmsStatusClass = pmsStatusLoading
    ? "checking"
    : hasSupportedPms && pmsLinked
      ? "connected"
      : "disconnected";
  const initials =
    displayName && displayName !== "Account"
      ? displayName
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase() || "")
          .join("")
      : "TH";
  const memberSince = user?.createdDate
    ? new Date(user.createdDate).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : "N/A";

  return (
    <div
      className={`layout-wrapper layout-static ${mobileOpen ? "layout-mobile-active" : ""} ${sidebarCollapsed ? "layout-sidebar-collapsed" : ""}`}
    >
      <div className="layout-topbar">
        <button
          type="button"
          className="p-link layout-menu-button layout-topbar-button"
          onClick={() => setMobileOpen((value) => !value)}
          aria-label="Toggle menu"
        >
          <i className="pi pi-bars" />
        </button>
        <div className={`layout-topbar-pms-status ${pmsStatusClass}`}>
          <span aria-hidden="true" />
          <p>{pmsStatusLabel}</p>
        </div>
        <button
          type="button"
          className="p-link layout-topbar-menu-button layout-topbar-button"
          onClick={() => setMobileTopbarOpen((value) => !value)}
          aria-label="Toggle topbar actions"
        >
          <i className="pi pi-ellipsis-v" />
        </button>
        <div
          className={`layout-topbar-menu ${mobileTopbarOpen ? "layout-topbar-menu-mobile-active" : ""}`}
        >
          <button
            type="button"
            onClick={() => goTo("/checkscore")}
            className="p-link layout-topbar-primary-action"
          >
            <i className="pi pi-plus" />
            <span>Guest Verification Form</span>
          </button>
          <button
            type="button"
            onClick={() => goTo("/account")}
            className="p-link layout-topbar-button displayable"
          >
            <i className="pi pi-user" />
            <span>{displayName}</span>
          </button>
          <button
            type="button"
            onClick={() => goTo("/topup")}
            className="p-link layout-topbar-button displayable"
          >
            <i className="pi pi-wallet" />
            <span>{screeningCredits} Credits Available</span>
          </button>
          <button
            type="button"
            onClick={logout}
            className="p-link layout-topbar-button"
          >
            <i className="pi pi-sign-out" />
            <span>Log Out</span>
          </button>
        </div>
      </div>

      <aside className="layout-sidebar">
        <div className="layout-sidebar-brand">
          <div className="layout-sidebar-brand-mark">
            <img src={trustdLogo} alt="Trustd.ai" />
            <span>Next Generation Guest Intelligence</span>
          </div>
          <button
            type="button"
            className="p-link layout-sidebar-collapse"
            onClick={() => setSidebarCollapsed((value) => !value)}
            aria-label={
              sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
            }
            aria-expanded={!sidebarCollapsed}
          >
            <i className="pi pi-bars" />
          </button>
        </div>
        <div className="layout-sidebar-head">
          <div className="layout-sidebar-user-card">
            <div className="layout-sidebar-avatar">{initials}</div>
            <div>
              <p className="layout-sidebar-user-name">{displayName}</p>
              <p className="layout-sidebar-user-meta">
                Member since {memberSince}
              </p>
            </div>
          </div>
        </div>
        <nav className="layout-sidebar-inner">
          <ul className="layout-menu">
            {menuItems.map((item) => (
              <li key={item.to} className="layout-root-menuitem">
                <button
                  type="button"
                  title={sidebarCollapsed ? item.label : undefined}
                  className={
                    location.pathname === item.to ? "active-route" : ""
                  }
                  onClick={() => {
                    goTo(item.to);
                  }}
                >
                  <i className={`${item.icon} layout-menuitem-icon`} />
                  <span className="layout-menuitem-text">{item.label}</span>
                </button>
              </li>
            ))}
            <li className="layout-root-menuitem layout-menu-separator">
              <button
                type="button"
                onClick={logout}
                title={sidebarCollapsed ? "Log Out" : undefined}
              >
                <i className="pi pi-sign-out layout-menuitem-icon" />
                <span className="layout-menuitem-text">Log Out</span>
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="layout-main-container">
        <div className="layout-main">{children}</div>
      </main>
      <div className="layout-mask" onClick={() => setMobileOpen(false)} />
    </div>
  );
}
